import { api, ProjectDetail } from '../api';
import { openModal } from './modal';
import { toast } from './toast';
import { escapeHtml, formatDate } from '../lib/utils';

interface ShareInfo {
  token: string;
  expiresAt: string;
  url: string;
}

const EXPIRY_OPTIONS = [
  { value: '1', label: '1 day' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
];

export function openShareModal(project: ProjectDetail): void {
  openModal(
    `
    <h2 style="font-size:20px;margin-bottom:8px;">Share project</h2>
    <p class="muted" style="margin:0 0 16px;">
      Anyone with this link can view <strong>${escapeHtml(
        project.name
      )}</strong> without logging in. They will not be able to make changes.
    </p>
    <div id="share-modal-body"><div class="spinner">Loading share link…</div></div>
  `,
    async (body) => {
      const shareBody = body.querySelector('#share-modal-body') as HTMLElement;

      let share: ShareInfo | null = null;
      try {
        const res = await api.get<{ share: ShareInfo | null }>(
          `/projects/${project.id}/share`
        );
        share = res.share;
      } catch (e: any) {
        shareBody.innerHTML = `
          <div class="error-state">
            <h3>Could not load share link</h3>
            <p class="muted">${escapeHtml(e.message)}</p>
          </div>
        `;
        return;
      }

      renderShare(shareBody, share);

      function renderShare(container: HTMLElement, current: ShareInfo | null): void {
        container.innerHTML = `
          ${
            current
              ? `
            <div class="field">
              <label>Share link</label>
              <div style="display:flex;gap:10px;">
                <input class="input" id="share-url" readonly value="${escapeHtml(
                  current.url
                )}" />
                <button class="btn btn-ghost" id="copy-share">Copy</button>
              </div>
              <p class="muted" style="font-size:13px;margin:8px 0 0;">
                Expires ${formatDate(current.expiresAt)}
              </p>
            </div>
          `
              : `
            <p class="muted" style="margin-bottom:12px;">
              No share link yet. Create one to give read-only access.
            </p>
          `
          }
          <div class="field">
            <label>Link expires in</label>
            <select class="select" id="share-expiry">
              ${EXPIRY_OPTIONS.map(
                (o) =>
                  `<option value="${o.value}" ${o.value === '7' ? 'selected' : ''}>${o.label}</option>`
              ).join('')}
            </select>
          </div>
          <div class="form-error hidden" id="share-error"></div>
          <div style="display:flex;gap:10px;justify-content:flex-end;align-items:center;">
            ${
              current
                ? `<button class="btn btn-danger" id="revoke-share" style="margin-right:auto;">Revoke link</button>`
                : ''
            }
            <button class="btn btn-primary" id="generate-share">
              ${current ? 'Regenerate link' : 'Create link'}
            </button>
          </div>
        `;

        container.querySelector('#copy-share')?.addEventListener('click', () =>
          copyLink(current!.url)
        );

        container.querySelector('#generate-share')!.addEventListener('click', async () => {
          const errorEl = container.querySelector('#share-error') as HTMLElement;
          errorEl.classList.add('hidden');
          const select = container.querySelector('#share-expiry') as HTMLSelectElement;
          const expiresInDays = Number(select.value) || 7;
          try {
            const res = await api.post<{ share: ShareInfo }>(
              `/projects/${project.id}/share`,
              { expiresInDays }
            );
            share = res.share;
            toast('Share link created');
            renderShare(container, share);
          } catch (e: any) {
            errorEl.textContent = e.message || 'Could not create share link';
            errorEl.classList.remove('hidden');
          }
        });

        container.querySelector('#revoke-share')?.addEventListener('click', async () => {
          if (!confirm('Revoke this share link? Anyone using it will lose access immediately.')) {
            return;
          }
          try {
            await api.del(`/projects/${project.id}/share`);
            share = null;
            toast('Share link revoked');
            renderShare(container, null);
          } catch (e: any) {
            toast(e.message || 'Could not revoke share link', 'error');
          }
        });
      }
    }
  );
}

async function copyLink(url: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
    toast('Link copied to clipboard');
  } catch {
    const input = document.createElement('input');
    input.value = url;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
    toast('Link copied to clipboard');
  }
}
